import { router, useRootNavigationState } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';

import { NpoOnboardingRoutes } from '@/constants/enums/NpoOnboardingRoutes';
import { useAuth } from '@/contexts/authContext';
import { useNpoLoginScreenState } from '@/contexts/npoLoginScreenStateContext';
import { AppStorage } from '@/utils/AppStorage';
import { safeJsonParse } from '@/utils/jsonParse';
import { npoOnboardingWhereToRoute } from '@/utils/npoOnboardingWhereToRoute';
import { clearPendingLink, getPendingLink, useDeepLinkStore } from '@/deepLinks/deepLinkStore';

const isWebView = () => {
  if (typeof window === 'undefined') return false;

  if ((window as any).ReactNativeWebView) return true;
};

const useAuthMiddleware = () => {
  const { state, dispatch: dispatchAuth } = useAuth();
  const { setTotalPages, setCurrentPage, completeOnboarding } = useNpoLoginScreenState();
  const { pendingLink: deepLink } = useDeepLinkStore();
  const [hasRouted, setHasRouted] = useState(false);
  const hasProcessedDeepLink = useRef(false);

  const rootNavigationState = useRootNavigationState();

  useEffect(() => {
    if (deepLink) {
      hasProcessedDeepLink.current = false;
    }
  }, [deepLink]);

  useEffect(() => {
    if (__DEV__) console.log('[DeepLink]:', deepLink);

    if (typeof window === 'undefined') return;
    if (!rootNavigationState?.key) return;

    const freshDeepLink = getPendingLink();

    if (state.userId && state.authReady) {
      // INDIVIDUAL LOGIN
      if (state?.providerId === 'phone') {
        if (freshDeepLink) {
          if (freshDeepLink.pathname.includes('purchaseShow')) {
            dispatchAuth({
              type: 'BUYING_SHOW',
              isBuyingShow: true
            });
            if (freshDeepLink.params && freshDeepLink.params?.influencerCode) {
              AppStorage.setItem(
                'influencerCode',
                safeJsonParse<{ influencerCode: string }>(freshDeepLink.params?.influencerCode ?? '')?.influencerCode ??
                  ''
              );
            }
            clearPendingLink();
          }
        }
        // User Not Onboarded
        if (state.user === undefined) {
          router.replace({
            pathname: '/login/individual/profile',
            params: {
              buyingShow: state.isBuyingShow ? 'true' : 'false'
            }
          });
          setHasRouted(true);
        } else {
          // User Onboarding Finished
          if (!!state.user.onBoardingPreference?.isSetEULAScreen) {
            if (freshDeepLink) {
              hasProcessedDeepLink.current = true;
              router.replace('/home/npoTabs');
              if (freshDeepLink?.params?.type && freshDeepLink?.params?.id) {
                requestAnimationFrame(() => {
                  router.push({
                    pathname: '/(app)/messages',
                    params: {
                      roomId: freshDeepLink.params?.id
                    }
                  });
                });
                // Remove deeplink after use to avoid multiple uses

                clearPendingLink();
                setHasRouted(true);
              } else {
                requestAnimationFrame(() => {
                  router.push({
                    pathname: freshDeepLink.pathname as any,
                    params: freshDeepLink.params
                  });
                });
                // Remove deeplink after use to avoid multiple uses

                clearPendingLink();
                setHasRouted(true);
              }

              // Ensures that we don't have deep link navigation
            } else if (!freshDeepLink && !hasProcessedDeepLink.current) {
              if (state.isBuyingShow) {
                router.replace('/home/npoTabs');
                requestAnimationFrame(() => {
                  router.push({
                    pathname: '/(app)/purchaseShow'
                  });
                });
                setHasRouted(true);
              } else {
                router.replace('/home/npoTabs');
                setHasRouted(true);
              }
            }
            // User Onboarding In Progress
          } else if (!!state.user.onBoardingPreference?.isSetPreferenceScreen) {
            router.replace({
              pathname: '/login/individual/eula',
              params: {
                onBoardingId: state.user.onBoardingPreference.id,
                buyingShow: state.isBuyingShow ? 'true' : 'false'
              }
            });

            setHasRouted(true);
          } else if (!!state.user.onBoardingPreference?.isSetLocationScreen) {
            router.replace({
              pathname: '/login/individual/interests',
              params: {
                onBoardingId: state.user.onBoardingPreference.id,
                buyingShow: state.isBuyingShow ? 'true' : 'false'
              }
            });

            setHasRouted(true);
          } else if (!!state.user.onBoardingPreference) {
            router.replace({
              pathname: '/login/individual/distances',
              params: {
                onBoardingId: state.user.onBoardingPreference.id,
                buyingShow: state.isBuyingShow ? 'true' : 'false'
              }
            });
            setHasRouted(true);
          }
        }
      } else if (state?.providerId === 'password') {
        // NPO LOGIN
        // Only Navigate to the logued screens if the login request is done
        if (state.user && state.signUpData?.loginEmail !== undefined) {
          if (state.user.user.nonProfitOrgId === null) {
            router.replace('/login/nonProfit/findNpo');
            setHasRouted(true);
          } else {
            // User Onboarding In Progress
            const type = state.signUpData?.loginEmail?.type;
            const preferences =
              type === 'CHARITIES' ? state.user.onBoardingPreferenceSecular : state.user.onBoardingPreferenceChurch;
            if (preferences !== null && preferences && state.signUpData?.loginEmail) {
              const whereToRoute = npoOnboardingWhereToRoute(type, preferences);

              switch (whereToRoute) {
                case NpoOnboardingRoutes.PREFERENCES:
                  router.replace({
                    pathname: whereToRoute
                  });
                  setHasRouted(true);
                  break;

                case NpoOnboardingRoutes.NOTIFICATIONS:
                  router.replace({
                    pathname: whereToRoute,
                    params: { from: 'nonProfit' }
                  });
                  setHasRouted(true);
                  break;

                case NpoOnboardingRoutes.LOCATION:
                  router.replace({
                    pathname: whereToRoute,
                    params: { from: 'nonProfit' }
                  });
                  setHasRouted(true);
                  break;

                // User Onboarding Finished
                case NpoOnboardingRoutes.PROFILE:
                  completeOnboarding();

                  setTotalPages(14);
                  setCurrentPage(14);

                  if (freshDeepLink) {
                    hasProcessedDeepLink.current = true;
                    router.replace({
                      pathname: whereToRoute
                    });
                    requestAnimationFrame(() => {
                      router.push({
                        pathname: freshDeepLink.pathname as any,
                        params: freshDeepLink.params
                      });
                    });

                    // Remove deeplink after use to avoid multiple uses
                    clearPendingLink();
                    setHasRouted(true);
                  } else if (!freshDeepLink && !hasProcessedDeepLink.current) {
                    router.replace({
                      pathname: whereToRoute
                    });
                    setHasRouted(true);
                  }
                  break;

                //First Show intro screen then navigate
                default:
                  router.replace({
                    pathname: '/login/nonProfit/intro',
                    params: {
                      onBoardingId: state.preferenceId,
                      whereToGoAfterIntro: whereToRoute
                    }
                  });
                  setHasRouted(true);
                  break;
              }
            }
          }
        }
      }
      // User is not loggued but have a deep link
    } else if (!state.userId && state.authReady) {
      if (freshDeepLink) {
        hasProcessedDeepLink.current = true;
        // Purchase show deeplink
        if (freshDeepLink.pathname.includes('purchaseShow')) {
          if (freshDeepLink.params?.influencerCode) {
            AppStorage.setItem('influencerCode', freshDeepLink.params.influencerCode);
          }
          router.push({
            pathname: '/login/individual',
            params: {
              buyingShow: 'true'
            }
          });
        } else {
          if (Platform.OS === 'web' && isWebView()) {
            if (freshDeepLink.params && Object.keys(freshDeepLink.params).length > 0) {
              const searchParams = new URLSearchParams(freshDeepLink.params as Record<string, string>).toString();
              window.location.href = `${freshDeepLink.pathname}?${searchParams}`;
            } else {
              window.location.href = freshDeepLink.pathname;
            }
          } else {
            router.navigate({
              pathname: freshDeepLink.pathname as any,
              params: freshDeepLink.params
            });
          }
        }

        clearPendingLink();
      }
      setHasRouted(true);
    }
  }, [state.userId, state.signUpData, state.authReady, rootNavigationState?.key, deepLink]);

  return {
    hasRouted
  };
};

export default useAuthMiddleware;
